import { DEFAULT_GRADING_CONFIG } from "./config.js";
/** Maps a window score onto a grade. */
export function gradeForScore(score, config = DEFAULT_GRADING_CONFIG) {
    if (score >= config.thresholds.perfect) {
        return "perfect";
    }
    if (score >= config.thresholds.great) {
        return "great";
    }
    if (score >= config.thresholds.good) {
        return "good";
    }
    return "miss";
}
/**
 * Mean after discarding the worst `trimFraction` of values.
 *
 * A single badly tracked frame in the middle of a good move should not decide
 * the grade for that whole second. Trimming the low end rather than using a
 * plain mean keeps one bad sample from dominating.
 */
export function trimmedMean(values, trimFraction) {
    if (values.length === 0) {
        return 0;
    }
    if (trimFraction <= 0 || values.length === 1) {
        return values.reduce((sum, value) => sum + value, 0) / values.length;
    }
    const sorted = [...values].sort((a, b) => a - b);
    // Always keep at least one sample, however aggressive the trim.
    const dropCount = Math.min(sorted.length - 1, Math.floor(sorted.length * trimFraction));
    const kept = sorted.slice(dropCount);
    return kept.reduce((sum, value) => sum + value, 0) / kept.length;
}
/**
 * Scoring windows for a choreography.
 *
 * Uses the choreography's own windows when present, so move-aligned grading can
 * arrive later without touching this code; otherwise falls back to fixed-length
 * windows starting at t=0.
 */
export function buildScoringWindows(choreography, config = DEFAULT_GRADING_CONFIG) {
    if (choreography.windows && choreography.windows.length > 0) {
        return choreography.windows.map((window) => ({ ...window }));
    }
    const lastFrame = choreography.frames[choreography.frames.length - 1];
    const durationMs = lastFrame?.tMs ?? 0;
    const windows = [];
    for (let startMs = 0; startMs <= durationMs; startMs += config.gradeWindowMs) {
        windows.push({ startMs, endMs: startMs + config.gradeWindowMs });
    }
    return windows;
}
function averageParts(samples) {
    const empty = {
        leftArm: 0,
        rightArm: 0,
        leftLeg: 0,
        rightLeg: 0,
        torso: 0,
        motion: 0,
    };
    if (samples.length === 0) {
        return empty;
    }
    const keys = Object.keys(empty);
    const totals = { ...empty };
    const confidenceTotals = { ...empty };
    for (const sample of samples) {
        for (const key of keys) {
            const confidence = sample.result.partConfidence[key];
            totals[key] += sample.result.parts[key] * confidence;
            confidenceTotals[key] += confidence;
        }
    }
    for (const key of keys) {
        totals[key] = confidenceTotals[key] > 0 ? totals[key] / confidenceTotals[key] : 0;
    }
    return totals;
}
function partCoverage(samples, totalSamples) {
    const coverage = {
        leftArm: 0,
        rightArm: 0,
        leftLeg: 0,
        rightLeg: 0,
        torso: 0,
        motion: 0,
    };
    if (totalSamples === 0) {
        return coverage;
    }
    for (const sample of samples) {
        for (const key of Object.keys(coverage)) {
            coverage[key] += sample.result.partConfidence[key] / totalSamples;
        }
    }
    return coverage;
}
/**
 * Aggregates scored samples into grade events.
 *
 * Combo and points are folded in here rather than tracked by the UI, so replays
 * and tests produce identical numbers to a live session.
 */
export function aggregateGradeEvents(samples, windows, config = DEFAULT_GRADING_CONFIG) {
    const events = [];
    let combo = 0;
    windows.forEach((window, windowIndex) => {
        const inWindow = samples.filter((sample) => sample.result.scorable && sample.tMs >= window.startMs && sample.tMs < window.endMs);
        // A window with no reference samples is not part of the routine.
        if (inWindow.length === 0) {
            return;
        }
        const score = trimmedMean(inWindow.map((sample) => sample.result.total), config.trimFraction);
        const grade = gradeForScore(score, config);
        combo = grade === "miss" ? 0 : combo + 1;
        const multiplier = Math.min(1 + combo * config.comboStep, config.comboCap);
        const points = config.gradePoints[grade] * multiplier;
        events.push({
            windowIndex,
            startMs: window.startMs,
            endMs: window.endMs,
            score,
            grade,
            combo,
            points,
            parts: averageParts(inWindow),
        });
    });
    return events;
}
/** Final summary shown after a run. */
export function summarizePerformance(events, samples, totalReferenceSamples = samples.length) {
    const grades = { perfect: 0, great: 0, good: 0, miss: 0 };
    let totalScore = 0;
    let maxCombo = 0;
    let scoreSum = 0;
    const scoredSamples = samples.filter((sample) => sample.result.scorable).length;
    for (const event of events) {
        grades[event.grade] += 1;
        totalScore += event.points;
        maxCombo = Math.max(maxCombo, event.combo);
        scoreSum += event.score;
    }
    return {
        totalScore: Math.round(totalScore),
        maxCombo,
        grades,
        // Averaged over samples, not windows: the breakdown answers "how was my
        // left arm overall", which is a property of the whole performance.
        parts: averageParts(samples),
        averageScore: events.length > 0 ? scoreSum / events.length : 0,
        scoredSamples,
        totalSamples: totalReferenceSamples,
        sampleCoverage: totalReferenceSamples > 0 ? scoredSamples / totalReferenceSamples : 0,
        partCoverage: partCoverage(samples, totalReferenceSamples),
    };
}
//# sourceMappingURL=grading.js.map